import React, { useEffect, useState } from "react";
import { AiOutlinePaperClip } from "react-icons/ai";
import { FaUpload, FaXmark } from "react-icons/fa6";
import ExpandableComponent from "../../components/BasicComponents/ExpandableComponent";
import {
  useGetExcelSheetsMutation,
  useUploadExcelSheetsMutation,
} from "./sql/TablesourceApiSlice"; // <- Make sure this hook exists
import { useNavigate } from "react-router-dom";

export default function UploadExcel({ closeModal }) {
  const [file, setFile] = useState(null);
  const [fileName, setFileName] = useState(null);
  const [uploadedExcelFileDetails, setUploadedExcelFileDetails] =
    useState(null);
  const [selectAll, setSelectAll] = useState(true);
  const [selectedSheets, setSelectedSheets] = useState([]);
  const [message, setMessage] = useState("");
  // console.log(selectedSheets);
  const navigate = useNavigate();

  const [
    getExcelSheets,
    {
      data: excelSheetData,
      isLoading: isExcelSheetLoading,
      isSuccess: isExcelSheetSuccess,
      isError: isExcelSheetError,
      error: excelSheetError,
    },
  ] = useGetExcelSheetsMutation();

  const [
    uploadExcelSheets,
    {
      data: uploadData,
      isLoading: isUploading,
      isSuccess: isUploadSuccess,
      isError: isUploadError,
      error: uploadError,
    },
  ] = useUploadExcelSheetsMutation(); // <- Create this mutation in your API slice

  const handleFileChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      setFile(file);
      setFileName(file.name);
    }
  };

  useEffect(() => {
    if (!file) return;
    const formData = new FormData();
    formData.append("file", file);
    getExcelSheets(formData);
  }, [file]);

  useEffect(() => {
    if (isExcelSheetSuccess && excelSheetData) {
      const sheetNames = excelSheetData["sheet_names"] || [];
      setUploadedExcelFileDetails({
        excelSheets: sheetNames,
        uploadId: excelSheetData["upload_id"],
      });
      setSelectedSheets(sheetNames); // default all selected
      setSelectAll(true);
    }
  }, [isExcelSheetSuccess, excelSheetData]);

  const handleCheckboxChange = (value) => {
    const newSheets = selectedSheets.includes(value)
      ? selectedSheets.filter((item) => item !== value)
      : [...selectedSheets, value];
    setSelectedSheets(newSheets);
    setSelectAll(
      newSheets.length === uploadedExcelFileDetails.excelSheets.length
    );
  };

  const handleSelectAll = (allSheets) => {
    const shouldSelectAll = !selectAll;
    if (shouldSelectAll) {
      setSelectedSheets(allSheets);
    } else {
      setSelectedSheets([]);
    }
    setSelectAll(shouldSelectAll);
  };

  const handleUpload = async () => {
    if (!selectedSheets.length) {
      setMessage("Please select at least one sheet.");
      return;
    }

    const payload = {
      upload_id: uploadedExcelFileDetails.uploadId,
      sheets: selectedSheets,
      table_prefix: "excel",
    };
    // console.log(payload);

    try {
      const response = await uploadExcelSheets(payload).unwrap();
      setMessage(response.message || "Upload successful.");
    } catch (err) {
      setMessage(err?.data?.error || "Something went wrong during upload.");
    }
  };
  useEffect(() => {
    if (message !== "" && isUploadSuccess && uploadData) {
      const timer = setTimeout(() => {
        // closeModal();
        // console.log(uploadData);
        const connectionDetailsObject =
          uploadData?.dataSource?.ConnectionDetails;
        const tableName = connectionDetailsObject?.TableName;
        const id = uploadData?.dataSource?._id;
        const DataBaseName = connectionDetailsObject?.DataBaseName;
        navigate(
          `/jointables?table=${tableName}&id=${id}&database=${DataBaseName}`
        );
      }, 1500);
      return () => clearTimeout(timer);
    }
  }, [isUploadSuccess, message]);

  return (
    <main className="fixed inset-0 flex items-center justify-center bg-gray-800 bg-opacity-50 z-50">
      <main className="h-auto max-h-[90%] w-[35%] bg-white flex flex-col shadow-xl gap-2 min-w-[35%] rounded">
        <header className="p-2 flex items-center justify-between rounded-t-lg bg-gray-50 font-medium tracking-wide">
          Excel Upload
          <button>
            <FaXmark onClick={closeModal} />
          </button>
        </header>

        <main className="h-[20rem] flex flex-col px-2 items-start justify-start w-full overflow-hidden overflow-y-auto">
          <ExpandableComponent
            Title="General Information"
            SubTitle="Upload an Excel file to a database"
            defaultValue={true}
          >
            <div className="w-full items-start justify-start flex gap-2 p-1">
              <label
                htmlFor="fileInput"
                className="flex items-center gap-2 px-2 py-[2px] bg-blue-950 text-white rounded cursor-pointer"
              >
                <FaUpload />
                select
              </label>
              <input
                type="file"
                id="fileInput"
                onChange={handleFileChange}
                accept=".xls,.xlsx"
                className="hidden"
              />
              {fileName && (
                <button className="text-gray-800 text-sm flex w-auto hover:bg-gray-100 items-center gap-1 p-[2px] rounded">
                  <AiOutlinePaperClip />
                  {fileName}
                </button>
              )}
            </div>

            {uploadedExcelFileDetails && (
              <section className="w-full flex flex-col gap-1 items-center justify-start">
                <h1 className="flex items-center justify-left font-light w-full">
                  Select the Sheets
                </h1>
                <div className="w-full flex flex-col gap-1 shadow items-center justify-start p-2 rounded border-[1px]">
                  <button
                    className="flex items-center justify-left font-light w-full"
                    onClick={() =>
                      handleSelectAll(uploadedExcelFileDetails.excelSheets)
                    }
                  >
                    <input
                      type="checkbox"
                      id="checkbox-all"
                      checked={selectAll}
                      readOnly
                      className="mr-[6px] checkbox-input"
                    />
                    <label
                      htmlFor="checkbox-all"
                      className="truncate cursor-pointer text-sm font-light"
                    >
                      All
                    </label>
                  </button>
                  <div
                    className={`w-full ${
                      uploadedExcelFileDetails.excelSheets.length <= 4
                        ? "h-auto"
                        : "h-[7rem]"
                    } overflow-y-auto`}
                  >
                    {uploadedExcelFileDetails.excelSheets.map(
                      (option, index) => (
                        <button
                          key={index}
                          className="cursor-pointer flex items-center mb-1 w-full truncate font-light"
                        >
                          <input
                            type="checkbox"
                            id={`checkbox-${index}`}
                            value={option}
                            checked={selectedSheets.includes(option)}
                            onChange={() => handleCheckboxChange(option)}
                            className="mr-[6px] checkbox-input cursor-pointer"
                          />
                          <label
                            htmlFor={`checkbox-${index}`}
                            className="truncate w-full text-left cursor-pointer text-sm font-light"
                          >
                            {option}
                          </label>
                        </button>
                      )
                    )}
                  </div>
                </div>
              </section>
            )}
          </ExpandableComponent>
        </main>

        <footer className="w-full items-center justify-between flex gap-4 p-2 bg-gray-50">
          <div className="text-sm text-left w-full">
            {isExcelSheetLoading && (
              <p className="text-yellow-500 font-medium">Loading sheets...</p>
            )}
            {isExcelSheetError && (
              <p className="text-red-500 font-medium">
                {excelSheetError?.data?.error || "Error loading Excel data"}
              </p>
            )}
            {message && (
              <p
                className={`font-medium ${
                  isUploadSuccess ? "text-green-500" : "text-red-500"
                }`}
              >
                {message}
              </p>
            )}
          </div>
          <div className="flex gap-2">
            <button
              className="border tracking-wide px-6 text-center py-[3px]"
              onClick={closeModal}
            >
              Cancel
            </button>
            <button
              className="border tracking-wide px-6 text-center py-[3px] bg-blue-950 text-white"
              onClick={handleUpload}
              disabled={isExcelSheetLoading || isUploading}
            >
              {isUploading ? "Uploading..." : "Upload"}
            </button>
          </div>
        </footer>
      </main>
    </main>
  );
}
